import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { usePromiseTracker } from 'react-promise-tracker';

import { useDebounce } from '../../../../../hooks';
import {
  PRODUCT_REFERENCE_DEFAULT_SIZE,
  PRODUCT_REFERENCE_LOAD_MORE_INCREMENT,
  PRODUCT_REFERENCE_MAX_SIZE,
  PRODUCT_REFERENCE_TRACKER_AREA,
} from '../constants';
import { getProdutctReference } from '../services';
import { useVisaoGeralElasticidadeStore } from '../VisaoGeralElasticidade.store';
import type { VisaoGeralElasticidadeFiltroProdutoBody, VisaoGeralElasticidadeFiltroProdutoResponse } from '../types';

const DEFAULT_PRODUCT_REFERENCE_FILTERS: VisaoGeralElasticidadeFiltroProdutoBody = {
  query: '',
  productBarcodes: [],
  brands: [],
  categories: [],
};

type ProductReferenceParams = {
  data: VisaoGeralElasticidadeFiltroProdutoBody;
  size?: number;
};

export type ProductReferenceOption = {
  label: string;
  value: string;
};

const mapProductReferenceToOptions = (products: VisaoGeralElasticidadeFiltroProdutoResponse[]): ProductReferenceOption[] =>
  products.map(product => ({
    label: `${product.gtin} - ${product.descricao || 'Sem descrição'}`,
    value: product.gtin,
  }));

export const useProductReference = () => {
  const [search, setSearch] = useState('');
  const [currentSize, setCurrentSize] = useState(PRODUCT_REFERENCE_DEFAULT_SIZE);
  const [isSearching, setIsSearching] = useState(false);
  const [productReferenceList, setProductReferenceList] = useState<VisaoGeralElasticidadeFiltroProdutoResponse[]>([]);

  const updateSharedFilters = useVisaoGeralElasticidadeStore.getState().updateSharedFilters;

  const debounce = useDebounce(500);
  const { promiseInProgress } = usePromiseTracker({ area: PRODUCT_REFERENCE_TRACKER_AREA });

  const isLoadingProductReference = isSearching || promiseInProgress;

  const getProductReference = async ({ data, size }: ProductReferenceParams) => {
    const response = await getProdutctReference(data, size ?? PRODUCT_REFERENCE_DEFAULT_SIZE);
    return response as VisaoGeralElasticidadeFiltroProdutoResponse[];
  };

  const mutation = useMutation({
    mutationFn: getProductReference,
    onSuccess: data => {
      setProductReferenceList(data);
    },
    onSettled: () => {
      setIsSearching(false);
    },
  });

  const { mutate, reset } = mutation;

  const fetchProductReference = useCallback(
    (query: string, size: number) => {
      mutate({
        data: {
          ...DEFAULT_PRODUCT_REFERENCE_FILTERS,
          query,
        },
        size,
      });
    },
    [mutate],
  );

  const productOptions = useMemo(() => mapProductReferenceToOptions(productReferenceList), [productReferenceList]);

  const handleProductReferenceSearch = useCallback(
    (value: string) => {
      setIsSearching(true);
      setSearch(value);
      setCurrentSize(PRODUCT_REFERENCE_DEFAULT_SIZE);

      debounce(() => {
        fetchProductReference(value, PRODUCT_REFERENCE_DEFAULT_SIZE);
      });
    },
    [debounce, fetchProductReference],
  );

  const handleLoadMore = useCallback(() => {
    if (productReferenceList.length >= PRODUCT_REFERENCE_MAX_SIZE) return;

    const newSize = currentSize + PRODUCT_REFERENCE_LOAD_MORE_INCREMENT;
    setCurrentSize(newSize);
    setIsSearching(true);
    fetchProductReference(search, newSize);
  }, [currentSize, fetchProductReference, productReferenceList.length, search]);

  const handleOpen = useCallback(() => {
    if (productReferenceList.length === 0) {
      setIsSearching(true);
      fetchProductReference('', PRODUCT_REFERENCE_DEFAULT_SIZE);
    }
  }, [fetchProductReference, productReferenceList.length]);

  const handleProductReferenceChange = useCallback(
    (value: string[]) => {
      updateSharedFilters({ productBarcodes: value });
    },
    [updateSharedFilters],
  );

  const resetProductReference = useCallback(() => {
    reset();
    setProductReferenceList([]);
    setSearch('');
    setCurrentSize(PRODUCT_REFERENCE_DEFAULT_SIZE);
    setIsSearching(false);
    updateSharedFilters({ productBarcodes: [] });
  }, [reset, updateSharedFilters]);

  useEffect(() => {
    setIsSearching(true);
    fetchProductReference('', PRODUCT_REFERENCE_DEFAULT_SIZE);
  }, [fetchProductReference]);

  return {
    productOptions,
    isLoadingProductReference,
    handleProductReferenceSearch,
    handleProductReferenceChange,
    handleLoadMore,
    handleOpen,
    maxSize: PRODUCT_REFERENCE_MAX_SIZE,
    error: mutation.error,
    resetProductReference,
  };
};
